import { useRef, useState, DragEvent } from 'react';
import { ArrowLeftIcon, ArrowUpIcon, XIcon } from 'lucide-react';
import { Mission } from '../../data/missions';
import HowToUse from '../HowToUse/HowToUse';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { calculateAmazonSales } from '../../api/amazonSalesApi';
import { useFileRun } from '../../hooks/useFileRun';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the Amazon settlement file (required).',
  'Optionally set an output filename, then hit Calculate Sales.',
  'The updated sales workbook downloads automatically once it is ready.',
];

interface AmazonSalesPanelProps {
  mission: Mission;
  onBack: () => void;
}

function AmazonSalesPanel({ mission, onBack }: AmazonSalesPanelProps) {
  const [settlementFile, setSettlementFile] = useState<File | null>(null);
  const [outputFilename, setOutputFilename] = useState('Amazon_Sales_Update.xlsx');
  const { status, error, result, fail, run } = useFileRun<{ blob: Blob; filename: string }>();

  const settlementInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleSubmit = async () => {
    if (!settlementFile) {
      fail('Upload the Amazon settlement file before calculating.');
      return;
    }
    await run(() => calculateAmazonSales(settlementFile, outputFilename), 'Calculation failed.');
  };

  return (
    <section
      style={{ borderTopColor: mission.color }}
      className="mx-auto max-w-[700px] rounded-2xl border border-border border-t-[6px] bg-card p-8 shadow-sm"
    >
      <Button
        variant="ghost"
        onClick={onBack}
        style={{ color: mission.color }}
        className="mb-5 h-auto gap-1.5 px-2 py-1.5 text-sm font-semibold hover:bg-transparent"
      >
        <ArrowLeftIcon className="size-5" /> All missions
      </Button>

      <div className="mb-6 flex items-start gap-4">
        <span
          style={{ backgroundColor: `${mission.color}26`, color: mission.color }}
          className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl"
        >
          <mission.icon className="h-7 w-7" />
        </span>
        <div>
          <h2 className="mb-1 text-xl font-bold text-foreground">{mission.title}</h2>
          <p className="text-sm text-muted-foreground">{mission.description}</p>
        </div>
      </div>

      <HowToUse steps={STEPS} />

      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-2 text-sm font-semibold text-foreground">📝 Expected file format</p>
        <p className="mb-2 text-sm text-muted-foreground">Your input file should contain:</p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>
            Standard orders date range (e.g., <em>Standard orders 03/03/2026 – 04/03/2026</em>)
          </li>
          <li>Product charges, Tax, Shipping, Promo rebates</li>
          <li>Refunded sales, Refunded expenses</li>
          <li>Inventory Reimbursements, FBA fees, Amazon fees, Advertising</li>
        </ul>
      </div>

      <div className="mb-5">
        <Label className="mb-2 text-sm font-semibold text-foreground">Settlement file (required)*</Label>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setDragActive(false);
            const dropped = e.dataTransfer.files && e.dataTransfer.files[0];
            if (dropped) setSettlementFile(dropped);
          }}
          className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
            dragActive ? 'border-current bg-muted' : 'border-input bg-muted'
          }`}
          style={dragActive ? { borderColor: mission.color } : undefined}
        >
          <Button
            onClick={() => settlementInputRef.current?.click()}
            style={{ backgroundColor: mission.color }}
            className="text-white hover:opacity-90"
          >
            <ArrowUpIcon /> Upload settlement file
          </Button>
          <span className="text-sm text-muted-foreground">or drag and drop a file here</span>
          <span className="text-xs text-muted-foreground">{mission.maxSizeLabel}</span>
          <input
            ref={settlementInputRef}
            type="file"
            accept={mission.accept}
            hidden
            onChange={(e) => {
              const picked = e.target.files && e.target.files[0];
              if (picked) setSettlementFile(picked);
              e.target.value = '';
            }}
          />
        </div>
        {settlementFile && (
          <div className="mt-2 flex items-center justify-between rounded-lg bg-green-50 px-4 py-2.5 text-sm text-green-700">
            <span>📄 {settlementFile.name}</span>
            <button
              type="button"
              onClick={() => setSettlementFile(null)}
              className="text-green-700 hover:text-green-900"
              aria-label="Remove settlement file"
            >
              <XIcon className="size-4" />
            </button>
          </div>
        )}
      </div>

      <div className="mb-6 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="output-filename" className="mb-1.5">
          Output filename
        </Label>
        <Input
          id="output-filename"
          value={outputFilename}
          onChange={(e) => setOutputFilename(e.target.value)}
          placeholder="Amazon_Sales_Update.xlsx"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Calculating…' : 'Calculate Sales'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Calculating…'} />}

      {status === 'error' && error && (
        <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">⚠️ {error}</div>
      )}
      {status === 'success' && result && (
        <div className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
          ✅ {result.filename} downloaded.
        </div>
      )}
      {status === 'idle' && (
        <div className="mt-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700">
          📁 Upload an Amazon settlement file above to get started.
        </div>
      )}
    </section>
  );
}

export default AmazonSalesPanel;
